/**
 * Protected route wrapper that redirects to Django login if not authenticated.
 * Fresh loading state with animated spinner.
 */

import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, loginUrl } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center relative overflow-hidden">
        {/* Floating blobs */}
        <div className="blob blob-coral w-72 h-72 -top-36 -right-36 opacity-30" />
        <div className="blob blob-mint w-64 h-64 bottom-20 -left-32 opacity-30" />

        <div className="relative z-10 text-center animate-fade-in">
          {/* Animated logo */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-coral-400 via-coral-500 to-lavender-500 flex items-center justify-center shadow-soft animate-pulse-soft">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
          <p className="text-sage-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Redirect to Django login page
    window.location.href = loginUrl;
    return null;
  }

  return <>{children}</>;
}
